/**
 * CLI: `canary property skill` — manage a property's app skill
 *
 * App skill = the per-property knowledge base injected into the cloud workflow
 * browser agent's system prompt. CLI is a thin client over the existing
 * /org/properties/:id/context endpoints. Source defaults to 'cli' so authored
 * items are distinguishable from browser-bootstrap or human-manual edits.
 */

import { readFileSync } from 'node:fs';
import { writeRawOutput } from "./brand.js";
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest, getApplicationArg } from './cli-helpers.js';

const HERE = dirname(fileURLToPath(import.meta.url));
const GUIDE_PATH = join(HERE, 'property-skill-guide.md');

const SECTIONS = [
  'general',
  'technology',
  'product',
  'ui',
  'tenancy',
  'auth',
  'roles_permissions',
  'navigation',
  'feature_gating',
  'environments',
  'data_lifecycle',
  'async_realtime',
  'localization',
  'feedback_errors',
  'idiosyncrasies',
] as const;
type Section = (typeof SECTIONS)[number];

type ContextItem = {
  id: string;
  propertyId: string;
  section: Section;
  itemKey: string;
  itemLabel: string;
  content: string;
  version: number;
  source: 'bootstrap' | 'workflow' | 'manual' | 'cli';
  sourceId: string | null;
  createdBy: string | null;
  createdAt: string;
};

type ListResponse = {
  ok: boolean;
  error?: string;
  data?: { items: ContextItem[]; grouped: Record<string, ContextItem[]> };
};

function printSkillHelp(): void {
  console.log(
    [
      'canary application skill: Manage an application\'s app skill (per-application knowledge for QA browser agents). Alias: canary property skill. (--property accepted as an alias for --application.)',
      '',
      'Sub-commands:',
      '  guide                                                          Print the full authoring methodology (start here)',
      '  list      [--application <id>] [--section S] [--format json|table]',
      '  get       --application <id> --section S --key K [--version N] [--format json|md]',
      '  set       --application <id> --section S --key K --label L --content @file|"text" [--source-id <repo@sha>]',
      '  delete    --application <id> --section S --key K',
      '  history   --application <id> --section S --key K [--format json|table]',
      '  export    --application <id> [--format md|json]',
      '  import    --application <id> --file <bundle.json> [--source-id <repo@sha>]',
      '',
      'New to app skills? Run `canary application skill guide` for the authoring methodology.',
      '',
      'Sections:',
      '  ' + SECTIONS.join(', '),
      '',
      'Notes:',
      '  --content @path   reads content from a file (preferred for multi-line markdown)',
      '  --content "text"  literal content string',
      '  Source defaults to "cli". Provide --source-id <repo@sha> for provenance.',
      '',
      'Bundle.json shape for import:',
      '  { "items": [ { "section": S, "itemKey": K, "itemLabel": L, "content": "..." }, ... ] }',
    ].join('\n')
  );
}

function readContent(value: string): string {
  if (value.startsWith('@')) {
    const path = value.slice(1);
    return readFileSync(path, 'utf8');
  }
  return value;
}

function isSection(s: string | undefined): s is Section {
  return !!s && (SECTIONS as readonly string[]).includes(s);
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = getApplicationArg(argv);
  const section = getArgValue(argv, '--section');
  const format = getArgValue(argv, '--format') ?? (hasFlag(argv, '--json') ? 'json' : 'table');

  if (!propertyId) {
    console.error('Error: --application is required (alias: --property).');
    process.exit(1);
  }

  const result = await apiRequest<ListResponse>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/context`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  let items = result.data?.items ?? [];
  if (section) items = items.filter((i) => i.section === section);

  if (format === 'json') {
    console.log(JSON.stringify(items, null, 2));
    return;
  }

  if (items.length === 0) {
    console.log('(no items)');
    return;
  }

  const grouped = new Map<string, ContextItem[]>();
  for (const item of items) {
    const arr = grouped.get(item.section) ?? [];
    arr.push(item);
    grouped.set(item.section, arr);
  }

  for (const sec of SECTIONS) {
    const arr = grouped.get(sec);
    if (!arr || arr.length === 0) continue;
    console.log(`\n[${sec}]`);
    for (const item of arr) {
      const provenance = item.source === 'cli' && item.sourceId ? ` (${item.sourceId})` : item.source !== 'cli' ? ` (${item.source})` : '';
      // itemKey/itemLabel/provenance are customer-controlled — same class as
      // the record view above, so the whole line renders raw.
      writeRawOutput(`  ${item.itemKey} — ${item.itemLabel}${provenance}`);
    }
  }
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = getApplicationArg(argv);
  const section = getArgValue(argv, '--section');
  const itemKey = getArgValue(argv, '--key');
  const versionArg = getArgValue(argv, '--version');
  const format = getArgValue(argv, '--format') ?? 'md';

  if (!propertyId || !section || !itemKey) {
    console.error('Error: --application, --section, --key are required.');
    process.exit(1);
  }
  if (!isSection(section)) {
    console.error(`Error: invalid section "${section}". One of: ${SECTIONS.join(', ')}`);
    process.exit(1);
  }

  // Current value comes from list; specific version from history.
  if (versionArg) {
    const v = Number(versionArg);
    const result = await apiRequest<{ ok: boolean; error?: string; data?: ContextItem[] }>(
      apiUrl,
      token,
      'GET',
      `/org/properties/${propertyId}/context/history?section=${section}&itemKey=${encodeURIComponent(itemKey)}`
    );
    if (!result.ok) {
      console.error(`Error: ${result.error ?? 'Unknown error'}`);
      process.exit(1);
    }
    const item = (result.data ?? []).find((i) => i.version === v);
    if (!item) {
      console.error(`Error: no version ${v} for ${section}/${itemKey}`);
      process.exit(1);
    }
    printItem(item, format);
    return;
  }

  const result = await apiRequest<ListResponse>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/context`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  const item = (result.data?.items ?? []).find(
    (i) => i.section === section && i.itemKey === itemKey
  );
  if (!item) {
    console.error(`Error: no item ${section}/${itemKey}`);
    process.exit(1);
  }
  printItem(item, format);
}

function printItem(item: ContextItem, format: string): void {
  if (format === 'json') {
    writeRawOutput(JSON.stringify(item, null, 2));
    return;
  }
  // Every field here is customer-controlled (labels, keys, source ids, body),
  // so the WHOLE record renders raw — branding any part of it corrupts values.
  writeRawOutput(`# ${item.itemLabel}`);
  writeRawOutput(`section: ${item.section}`);
  writeRawOutput(`key: ${item.itemKey}`);
  writeRawOutput(`version: ${item.version}`);
  writeRawOutput(`source: ${item.source}${item.sourceId ? ` (${item.sourceId})` : ''}`);
  writeRawOutput('');
  writeRawOutput(item.content);
}

async function handleSet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = getApplicationArg(argv);
  const section = getArgValue(argv, '--section');
  const itemKey = getArgValue(argv, '--key');
  const itemLabel = getArgValue(argv, '--label');
  const contentArg = getArgValue(argv, '--content');
  const sourceId = getArgValue(argv, '--source-id');

  if (!propertyId || !section || !itemKey || !itemLabel || contentArg === undefined) {
    console.error('Error: --application, --section, --key, --label, --content are required.');
    process.exit(1);
  }
  if (!isSection(section)) {
    console.error(`Error: invalid section "${section}". One of: ${SECTIONS.join(', ')}`);
    process.exit(1);
  }

  const content = readContent(contentArg);

  const body: Record<string, unknown> = {
    section,
    itemKey,
    itemLabel,
    content,
    source: 'cli',
  };
  if (sourceId !== undefined) body.sourceId = sourceId;

  const result = await apiRequest<{ ok: boolean; error?: string; data?: ContextItem }>(
    apiUrl,
    token,
    'PUT',
    `/org/properties/${propertyId}/context/items`,
    body
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  console.log(`Set [${section}] ${itemKey} (v${result.data?.version ?? '?'})`);
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = getApplicationArg(argv);
  const section = getArgValue(argv, '--section');
  const itemKey = getArgValue(argv, '--key');

  if (!propertyId || !section || !itemKey) {
    console.error('Error: --application, --section, --key are required.');
    process.exit(1);
  }
  if (!isSection(section)) {
    console.error(`Error: invalid section "${section}". One of: ${SECTIONS.join(', ')}`);
    process.exit(1);
  }

  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'DELETE',
    `/org/properties/${propertyId}/context/items`,
    { section, itemKey }
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  console.log(`Deleted [${section}] ${itemKey}`);
}

async function handleHistory(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = getApplicationArg(argv);
  const section = getArgValue(argv, '--section');
  const itemKey = getArgValue(argv, '--key');
  const format = getArgValue(argv, '--format') ?? 'table';

  if (!propertyId || !section || !itemKey) {
    console.error('Error: --application, --section, --key are required.');
    process.exit(1);
  }
  if (!isSection(section)) {
    console.error(`Error: invalid section "${section}". One of: ${SECTIONS.join(', ')}`);
    process.exit(1);
  }

  const result = await apiRequest<{ ok: boolean; error?: string; data?: ContextItem[] }>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/context/history?section=${section}&itemKey=${encodeURIComponent(itemKey)}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  const versions = result.data ?? [];
  if (format === 'json') {
    console.log(JSON.stringify(versions, null, 2));
    return;
  }
  if (versions.length === 0) {
    console.log('(no history)');
    return;
  }
  for (const v of versions) {
    const provenance = v.sourceId ? ` (${v.sourceId})` : '';
    console.log(`v${v.version}  ${v.source}${provenance}  ${v.createdAt}`);
  }
}

async function handleExport(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = getApplicationArg(argv);
  const format = getArgValue(argv, '--format') ?? 'md';

  if (!propertyId) {
    console.error('Error: --application is required (alias: --property).');
    process.exit(1);
  }

  const result = await apiRequest<ListResponse>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/context`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  const items = result.data?.items ?? [];
  if (format === 'json') {
    console.log(JSON.stringify({ items }, null, 2));
    return;
  }

  const grouped = new Map<string, ContextItem[]>();
  for (const item of items) {
    const arr = grouped.get(item.section) ?? [];
    arr.push(item);
    grouped.set(item.section, arr);
  }
  for (const sec of SECTIONS) {
    const arr = grouped.get(sec);
    if (!arr || arr.length === 0) continue;
    console.log(`## ${sec}`);
    console.log('');
    for (const item of arr) {
      console.log(`### ${item.itemLabel} (\`${item.itemKey}\`)`);
      console.log('');
      console.log(item.content);
      console.log('');
    }
  }
}

type BundleItem = { section: string; itemKey: string; itemLabel: string; content: string };
type Bundle = { items: BundleItem[] };

async function handleImport(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = getApplicationArg(argv);
  const file = getArgValue(argv, '--file');
  const sourceId = getArgValue(argv, '--source-id');

  if (!propertyId || !file) {
    console.error('Error: --application and --file are required.');
    process.exit(1);
  }

  let bundle: Bundle;
  try {
    bundle = JSON.parse(readFileSync(file, 'utf8')) as Bundle;
  } catch (err) {
    console.error(`Error parsing ${file}: ${(err as Error).message}`);
    process.exit(1);
    return;
  }

  if (!bundle.items || !Array.isArray(bundle.items)) {
    console.error('Error: bundle.json must have an "items" array.');
    process.exit(1);
  }

  let ok = 0;
  let fail = 0;
  for (const item of bundle.items) {
    if (!isSection(item.section)) {
      console.error(`  ✗ [${item.section}] ${item.itemKey}: invalid section`);
      fail++;
      continue;
    }
    const body: Record<string, unknown> = {
      section: item.section,
      itemKey: item.itemKey,
      itemLabel: item.itemLabel,
      content: item.content,
      source: 'cli',
    };
    if (sourceId) body.sourceId = sourceId;

    const result = await apiRequest<{ ok: boolean; error?: string; data?: ContextItem }>(
      apiUrl,
      token,
      'PUT',
      `/org/properties/${propertyId}/context/items`,
      body
    );

    if (result.ok) {
      console.log(`  ✓ [${item.section}] ${item.itemKey} (v${result.data?.version ?? '?'})`);
      ok++;
    } else {
      console.error(`  ✗ [${item.section}] ${item.itemKey}: ${result.error ?? 'Unknown error'}`);
      fail++;
    }
  }

  console.log(`\nImported ${ok}/${ok + fail} items${fail ? ` (${fail} failed)` : ''}.`);
  if (fail > 0) process.exit(1);
}

function handleGuide(): void {
  try {
    const guide = readFileSync(GUIDE_PATH, 'utf8');
    process.stdout.write(guide);
    if (!guide.endsWith('\n')) process.stdout.write('\n');
  } catch (err) {
    console.error(`Error: could not read authoring guide at ${GUIDE_PATH}`);
    console.error((err as Error).message);
    process.exit(1);
  }
}

export async function runPropertySkill(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [action, ...rest] = argv;

  if (!action || action === 'help' || hasFlag(argv, '--help', '-h')) {
    printSkillHelp();
    return;
  }

  switch (action) {
    case 'guide':
      handleGuide();
      return;
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'get':
      await handleGet(rest, apiUrl, token);
      break;
    case 'set':
      await handleSet(rest, apiUrl, token);
      break;
    case 'delete':
      await handleDelete(rest, apiUrl, token);
      break;
    case 'history':
      await handleHistory(rest, apiUrl, token);
      break;
    case 'export':
      await handleExport(rest, apiUrl, token);
      break;
    case 'import':
      await handleImport(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown skill action: ${action}`);
      printSkillHelp();
      process.exit(1);
  }
}
